import type { DietaryConcern, DietaryConcernReason, DietaryConcernSeverity, DietaryConcernTag } from '../../shared/menu';
import { findLanguage } from '../../shared/languages';

// Source-language questions the traveler shows the waiter on the hand-off
// screen. Hand-coded per QA's safety policy — never Gemini-generated.
//
// PENDING: native-speaker QA before public launch. The author + Claude
// produced these from multilingual training data; they should be reviewed
// by a native speaker per language. For languages without native-QA'd
// entries, the chip falls back to English with a visible "shown in English"
// warning so the traveler knows the waiter may not be able to read it —
// safer than silent drop, which gives false confidence.
//
// "preference" framing: polite question, shorter
// "strict" framing: medical/allergy, prefixed with "I have an allergy" or
// equivalent — softens directness in cultures where blunt "Is this X?" is
// rude AND signals to the kitchen this is a serious request, not a fussy one.
//
// Reason-aware strict variants (strictByReason):
// When the user has a captured reason (medical/celiac/halal/kosher/ethical),
// strictByReason is consulted first; if there's a variant for that reason
// in the user's source language, it's used. Otherwise we fall through to
// the regular strict[] table. ENGLISH-ONLY today — non-English reason
// variants are pending native-speaker QA and ship per language only after
// review. The fallback chain ensures every user gets SOMETHING.

type LangMap = Record<string, string>;
type SeverityMap = Partial<Record<DietaryConcernSeverity, LangMap>> & {
  strictByReason?: Partial<Record<DietaryConcernReason, LangMap>>;
};

const PHRASES: Record<DietaryConcernTag, SeverityMap> = {
  vegan: {
    preference: {
      en: 'Is this dish vegan? (no meat, fish, dairy, or eggs)',
      es: '¿Este plato es vegano? (sin carne, pescado, lácteos ni huevos)',
      fr: 'Ce plat est-il végane ? (sans viande, poisson, produits laitiers ni œufs)',
      it: 'Questo piatto è vegano? (senza carne, pesce, latticini o uova)',
      de: 'Ist dieses Gericht vegan? (ohne Fleisch, Fisch, Milchprodukte oder Eier)',
      pt: 'Este prato é vegano? (sem carne, peixe, laticínios ou ovos)',
      ja: 'この料理はヴィーガンですか？（肉・魚・乳製品・卵を含まない）',
      zh: '这道菜是纯素的吗？（不含肉、鱼、乳制品或蛋）',
      'zh-TW': '這道菜是純素的嗎？（不含肉、魚、乳製品或蛋）',
      ko: '이 음식은 비건인가요? (고기, 생선, 유제품, 계란 없음)',
      th: 'อาหารจานนี้เป็นวีแกนไหมคะ/ครับ (ไม่มีเนื้อ ปลา นม หรือไข่)',
      vi: 'Món này có thuần chay không? (không thịt, cá, sữa, trứng)',
    },
    strict: {
      en: 'I have a strict vegan diet for medical reasons. Please confirm with the kitchen this dish contains no meat, fish, dairy, or eggs.',
      es: 'Sigo una dieta vegana estricta por razones médicas. Por favor confirme con la cocina que este plato no contiene carne, pescado, lácteos ni huevos.',
      fr: 'Je suis un régime végane strict pour raisons médicales. Pourriez-vous confirmer avec la cuisine que ce plat ne contient ni viande, ni poisson, ni produits laitiers, ni œufs ?',
      it: 'Seguo una dieta vegana rigorosa per motivi medici. Potrebbe confermare con la cucina che questo piatto non contiene carne, pesce, latticini o uova?',
      de: 'Ich habe aus medizinischen Gründen eine strenge vegane Ernährung. Bitte bestätigen Sie mit der Küche, dass dieses Gericht kein Fleisch, keinen Fisch, keine Milchprodukte und keine Eier enthält.',
      pt: 'Sigo uma dieta vegana estrita por motivos médicos. Por favor confirme com a cozinha que este prato não contém carne, peixe, laticínios nem ovos.',
      ja: '医療上の理由で厳格なヴィーガンです。この料理に肉・魚・乳製品・卵が含まれていないか、厨房に確認していただけますか？',
      zh: '我因健康原因严格吃纯素。请向厨房确认这道菜不含肉、鱼、乳制品和蛋。',
      'zh-TW': '我因健康原因嚴格吃純素。請向廚房確認這道菜不含肉、魚、乳製品和蛋。',
      ko: '저는 의료상의 이유로 엄격한 비건 식단을 지킵니다. 이 음식에 고기, 생선, 유제품, 계란이 들어있지 않은지 주방에 확인해 주실 수 있나요?',
      th: 'ฉันทานวีแกนเคร่งครัดด้วยเหตุผลทางการแพทย์ กรุณาตรวจสอบกับห้องครัวว่าอาหารจานนี้ไม่มีเนื้อ ปลา นม และไข่',
      vi: 'Tôi ăn chay trường nghiêm ngặt vì lý do sức khỏe. Vui lòng xác nhận với bếp rằng món này không chứa thịt, cá, sữa hay trứng.',
    },
    strictByReason: {
      // Drops "for medical reasons" — Maya's complaint that ethical vegans
      // were being misrepresented. Other languages pending native QA.
      ethical: {
        en: 'I am strictly vegan — please confirm with the kitchen this dish contains no meat, fish, dairy, or eggs.',
      },
    },
  },

  vegetarian: {
    preference: {
      en: 'Is this dish vegetarian? (no meat or fish, including stock or sauce)',
      es: '¿Este plato es vegetariano? (sin carne ni pescado, ni en el caldo ni la salsa)',
      fr: 'Ce plat est-il végétarien ? (sans viande ni poisson, y compris dans le bouillon ou la sauce)',
      it: 'Questo piatto è vegetariano? (senza carne o pesce, anche nel brodo o nel sugo)',
      de: 'Ist dieses Gericht vegetarisch? (ohne Fleisch oder Fisch, auch nicht in Brühe oder Soße)',
      pt: 'Este prato é vegetariano? (sem carne nem peixe, incluindo no caldo ou no molho)',
      ja: 'この料理はベジタリアンですか？（出汁やソースにも肉や魚を含まない）',
      zh: '这道菜是素食吗？（高汤和酱料中也不含肉或鱼）',
      'zh-TW': '這道菜是素食嗎？（高湯和醬料中也不含肉或魚）',
      ko: '이 음식은 채식인가요? (육수나 소스에도 고기나 생선 없음)',
      th: 'อาหารจานนี้มังสวิรัติไหมคะ/ครับ (ไม่มีเนื้อหรือปลาในน้ำซุปและซอสด้วย)',
      vi: 'Món này có chay không? (kể cả trong nước dùng hay nước sốt cũng không có thịt cá)',
    },
    strict: {
      en: 'I am strictly vegetarian for religious reasons. Please confirm with the kitchen this dish contains no meat or fish — including in the stock, sauce, or seasoning.',
      es: 'Soy estrictamente vegetariano por motivos religiosos. Por favor confirme con la cocina que este plato no contiene carne ni pescado, incluido el caldo, la salsa o los condimentos.',
      fr: 'Je suis strictement végétarien pour raisons religieuses. Pourriez-vous confirmer avec la cuisine que ce plat ne contient ni viande, ni poisson — y compris dans le bouillon, la sauce ou l’assaisonnement ?',
      it: 'Sono rigorosamente vegetariano per motivi religiosi. Potrebbe confermare con la cucina che questo piatto non contiene carne o pesce — nemmeno nel brodo, nel sugo o nei condimenti?',
      de: 'Ich bin aus religiösen Gründen streng vegetarisch. Bitte bestätigen Sie mit der Küche, dass dieses Gericht kein Fleisch oder Fisch enthält — auch nicht in Brühe, Soße oder Gewürzen.',
      pt: 'Sou estritamente vegetariano por motivos religiosos. Por favor confirme com a cozinha que este prato não contém carne nem peixe — incluindo no caldo, molho ou tempero.',
      ja: '宗教上の理由で厳格なベジタリアンです。出汁・ソース・調味料を含めて、この料理に肉や魚が含まれていないか、厨房に確認していただけますか？',
      zh: '我因宗教原因严格吃素。请向厨房确认这道菜不含任何肉或鱼，包括高汤、酱料和调味料。',
      'zh-TW': '我因宗教原因嚴格吃素。請向廚房確認這道菜不含任何肉或魚，包括高湯、醬料和調味料。',
      ko: '저는 종교상 엄격한 채식주의자입니다. 이 음식에 고기나 생선이 들어있지 않은지(육수, 소스, 양념 포함) 주방에 확인해 주실 수 있나요?',
      th: 'ฉันทานมังสวิรัติเคร่งครัดด้วยเหตุผลทางศาสนา กรุณาตรวจสอบกับห้องครัวว่าอาหารจานนี้ไม่มีเนื้อหรือปลา รวมถึงในน้ำซุป ซอส หรือเครื่องปรุง',
      vi: 'Tôi ăn chay nghiêm ngặt vì lý do tôn giáo. Vui lòng xác nhận với bếp rằng món này không có thịt hay cá — kể cả trong nước dùng, nước sốt hay gia vị.',
    },
    strictByReason: {
      ethical: {
        en: 'I am strictly vegetarian — please confirm with the kitchen this dish contains no meat or fish, including in the stock, sauce, or seasoning.',
      },
    },
  },

  gluten_free: {
    preference: {
      en: 'Is this dish gluten-free? (no wheat, barley, rye, or soy sauce)',
      es: '¿Este plato es sin gluten? (sin trigo, cebada, centeno ni salsa de soja)',
      fr: 'Ce plat est-il sans gluten ? (sans blé, orge, seigle ni sauce soja)',
      it: 'Questo piatto è senza glutine? (senza grano, orzo, segale o salsa di soia)',
      de: 'Ist dieses Gericht glutenfrei? (ohne Weizen, Gerste, Roggen oder Sojasauce)',
      pt: 'Este prato é sem glúten? (sem trigo, cevada, centeio ou molho de soja)',
      ja: 'この料理はグルテンフリーですか？（小麦・大麦・ライ麦・醤油なし）',
      zh: '这道菜是无麸质的吗？（不含小麦、大麦、黑麦或酱油）',
      'zh-TW': '這道菜是無麩質的嗎？（不含小麥、大麥、黑麥或醬油）',
      ko: '이 음식은 글루텐이 없나요? (밀, 보리, 호밀, 간장 없음)',
      th: 'อาหารจานนี้ไม่มีกลูเตนไหมคะ/ครับ (ไม่มีข้าวสาลี ข้าวบาร์เลย์ ข้าวไรย์ หรือซอสถั่วเหลือง)',
      vi: 'Món này có không chứa gluten không? (không lúa mì, lúa mạch, lúa mạch đen hay nước tương)',
    },
    strict: {
      en: 'I have celiac disease — strict no gluten. Please confirm with the kitchen this contains no wheat, barley, rye, or soy sauce, and is not cooked on a surface used for those.',
      es: 'Tengo celiaquía — sin gluten estricto. Por favor confirme con la cocina que no contiene trigo, cebada, centeno ni salsa de soja, y que no se cocina en una superficie usada para esos.',
      fr: 'Je suis cœliaque — strict sans gluten. Pourriez-vous confirmer avec la cuisine que ce plat ne contient ni blé, ni orge, ni seigle, ni sauce soja, et qu’il n’est pas cuit sur une surface utilisée pour cela ?',
      it: 'Sono celiaco — niente glutine, rigorosamente. Potrebbe confermare con la cucina che non contiene grano, orzo, segale o salsa di soia, e che non è cucinato su una superficie usata per quegli ingredienti?',
      de: 'Ich habe Zöliakie — strikt glutenfrei. Bitte bestätigen Sie mit der Küche, dass dieses Gericht keinen Weizen, keine Gerste, keinen Roggen oder Sojasauce enthält und nicht auf einer Oberfläche zubereitet wird, die für diese verwendet wird.',
      pt: 'Tenho doença celíaca — sem glúten rigoroso. Por favor confirme com a cozinha que não contém trigo, cevada, centeio ou molho de soja, e que não é preparado numa superfície usada para esses.',
      ja: 'セリアック病です — 厳格にグルテン禁止。小麦・大麦・ライ麦・醤油が含まれず、それらに使われた調理面で調理されていないか、厨房に確認していただけますか？',
      zh: '我有乳糜泻——严格不含麸质。请向厨房确认不含小麦、大麦、黑麦或酱油，且没有在加工过这些原料的台面上烹调。',
      'zh-TW': '我有乳糜瀉——嚴格不含麩質。請向廚房確認不含小麥、大麥、黑麥或醬油，且沒有在加工過這些原料的檯面上烹調。',
      ko: '저는 셀리악병이 있어 글루텐을 절대 못 먹습니다. 이 음식에 밀, 보리, 호밀, 간장이 없고 이런 재료가 사용된 조리면에서 만들어지지 않았는지 주방에 확인해 주실 수 있나요?',
      th: 'ฉันเป็นโรคแพ้กลูเตน (celiac) — ไม่กินกลูเตนอย่างเด็ดขาด กรุณาตรวจสอบกับห้องครัวว่าไม่มีข้าวสาลี ข้าวบาร์เลย์ ข้าวไรย์ หรือซอสถั่วเหลือง และไม่ได้ปรุงบนพื้นผิวที่ใช้กับวัตถุดิบเหล่านี้',
      vi: 'Tôi bị bệnh celiac — tuyệt đối không gluten. Vui lòng xác nhận với bếp rằng món này không có lúa mì, lúa mạch, lúa mạch đen, nước tương, và không nấu trên bề mặt đã dùng cho các nguyên liệu đó.',
    },
  },

  no_nuts: {
    preference: {
      en: 'Does this dish contain nuts or peanuts?',
      es: '¿Este plato contiene frutos secos o cacahuetes?',
      fr: 'Ce plat contient-il des fruits à coque ou des arachides ?',
      it: 'Questo piatto contiene frutta a guscio o arachidi?',
      de: 'Enthält dieses Gericht Nüsse oder Erdnüsse?',
      pt: 'Este prato contém frutos secos ou amendoim?',
      ja: 'この料理にナッツやピーナッツは入っていますか？',
      zh: '这道菜含有坚果或花生吗？',
      'zh-TW': '這道菜含有堅果或花生嗎？',
      ko: '이 음식에 견과류나 땅콩이 들어있나요?',
      th: 'อาหารจานนี้มีถั่วเปลือกแข็งหรือถั่วลิสงไหมคะ/ครับ',
      vi: 'Món này có chứa các loại hạt hay đậu phộng không?',
    },
    strict: {
      en: 'I have a severe nut allergy — including peanuts. Please confirm with the kitchen this dish has no nuts, no peanut oil, and is not cooked alongside nut dishes.',
      es: 'Tengo una alergia grave a los frutos secos, incluyendo cacahuetes. Por favor confirme con la cocina que este plato no contiene frutos secos, ni aceite de cacahuete, y no se cocina junto a platos con frutos secos.',
      fr: 'J’ai une allergie grave aux fruits à coque, y compris les arachides. Pourriez-vous confirmer avec la cuisine que ce plat ne contient ni fruits à coque, ni huile d’arachide, et n’est pas préparé à côté de plats avec fruits à coque ?',
      it: 'Ho una grave allergia alla frutta a guscio, comprese le arachidi. Potrebbe confermare con la cucina che questo piatto non contiene frutta a guscio, olio di arachidi, e non è cotto vicino a piatti con frutta a guscio?',
      de: 'Ich habe eine schwere Nussallergie — auch Erdnüsse. Bitte bestätigen Sie mit der Küche, dass dieses Gericht keine Nüsse oder Erdnussöl enthält und nicht neben Nussgerichten zubereitet wird.',
      pt: 'Tenho alergia grave a frutos secos, incluindo amendoim. Por favor confirme com a cozinha que este prato não contém frutos secos, óleo de amendoim, e não é preparado ao lado de pratos com frutos secos.',
      ja: 'ナッツ（ピーナッツ含む）に重度のアレルギーがあります。この料理にナッツやピーナッツオイルが含まれず、ナッツ料理の近くで調理されていないか、厨房に確認していただけますか？',
      zh: '我对坚果（包括花生）有严重过敏。请向厨房确认这道菜不含坚果、不用花生油，并且没有与坚果菜品一起烹调。',
      'zh-TW': '我對堅果（包括花生）有嚴重過敏。請向廚房確認這道菜不含堅果、不用花生油，並且沒有與堅果菜品一起烹調。',
      ko: '저는 견과류(땅콩 포함)에 심한 알레르기가 있습니다. 이 음식에 견과류나 땅콩 기름이 없고, 견과류 음식과 함께 조리되지 않았는지 주방에 확인해 주실 수 있나요?',
      th: 'ฉันแพ้ถั่วอย่างรุนแรง (รวมถึงถั่วลิสง) กรุณาตรวจสอบกับห้องครัวว่าอาหารจานนี้ไม่มีถั่ว ไม่ใช้น้ำมันถั่วลิสง และไม่ได้ปรุงข้างๆ อาหารที่มีถั่ว',
      vi: 'Tôi bị dị ứng nặng với các loại hạt, kể cả đậu phộng. Vui lòng xác nhận với bếp rằng món này không có hạt, không dùng dầu đậu phộng và không được nấu cạnh các món có hạt.',
    },
  },

  no_dairy: {
    preference: {
      en: 'Does this dish contain dairy? (milk, cheese, butter, cream, yogurt)',
      es: '¿Este plato contiene lácteos? (leche, queso, mantequilla, nata, yogur)',
      fr: 'Ce plat contient-il des produits laitiers ? (lait, fromage, beurre, crème, yaourt)',
      it: 'Questo piatto contiene latticini? (latte, formaggio, burro, panna, yogurt)',
      de: 'Enthält dieses Gericht Milchprodukte? (Milch, Käse, Butter, Sahne, Joghurt)',
      pt: 'Este prato contém laticínios? (leite, queijo, manteiga, natas, iogurte)',
      ja: 'この料理に乳製品は入っていますか？（牛乳・チーズ・バター・クリーム・ヨーグルト）',
      zh: '这道菜含有乳制品吗？（牛奶、奶酪、黄油、奶油、酸奶）',
      'zh-TW': '這道菜含有乳製品嗎？（牛奶、起司、奶油、鮮奶油、優格）',
      ko: '이 음식에 유제품이 들어있나요? (우유, 치즈, 버터, 크림, 요거트)',
      th: 'อาหารจานนี้มีนมหรือผลิตภัณฑ์จากนมไหมคะ/ครับ (นม ชีส เนย ครีม โยเกิร์ต)',
      vi: 'Món này có chứa sữa hay các sản phẩm từ sữa không? (sữa, phô mai, bơ, kem, sữa chua)',
    },
    strict: {
      en: 'I have a dairy allergy. Please confirm with the kitchen this contains no milk, cheese, butter, cream, yogurt, or ghee.',
      es: 'Tengo alergia a los lácteos. Por favor confirme con la cocina que no contiene leche, queso, mantequilla, nata, yogur ni ghee.',
      fr: 'J’ai une allergie aux produits laitiers. Pourriez-vous confirmer avec la cuisine que ce plat ne contient ni lait, ni fromage, ni beurre, ni crème, ni yaourt, ni ghee ?',
      it: 'Ho un’allergia ai latticini. Potrebbe confermare con la cucina che non contiene latte, formaggio, burro, panna, yogurt o ghee?',
      de: 'Ich habe eine Milchallergie. Bitte bestätigen Sie mit der Küche, dass dieses Gericht weder Milch, Käse, Butter, Sahne, Joghurt noch Ghee enthält.',
      pt: 'Tenho alergia a laticínios. Por favor confirme com a cozinha que não contém leite, queijo, manteiga, natas, iogurte ou ghee.',
      ja: '乳製品アレルギーがあります。牛乳・チーズ・バター・クリーム・ヨーグルト・ギーが含まれていないか、厨房に確認していただけますか？',
      zh: '我对乳制品过敏。请向厨房确认不含牛奶、奶酪、黄油、奶油、酸奶或酥油（ghee）。',
      'zh-TW': '我對乳製品過敏。請向廚房確認不含牛奶、起司、奶油、鮮奶油、優格或酥油（ghee）。',
      ko: '유제품 알레르기가 있습니다. 우유, 치즈, 버터, 크림, 요거트, 기(ghee)가 없는지 주방에 확인해 주실 수 있나요?',
      th: 'ฉันแพ้นมและผลิตภัณฑ์จากนม กรุณาตรวจสอบกับห้องครัวว่าไม่มีนม ชีส เนย ครีม โยเกิร์ต หรือกี (ghee)',
      vi: 'Tôi bị dị ứng sữa. Vui lòng xác nhận với bếp rằng món này không có sữa, phô mai, bơ, kem, sữa chua hay bơ ghee.',
    },
  },

  no_pork: {
    preference: {
      en: 'Does this dish contain pork or pork products?',
      es: '¿Este plato contiene cerdo o productos derivados del cerdo?',
      fr: 'Ce plat contient-il du porc ou des produits dérivés du porc ?',
      it: 'Questo piatto contiene maiale o prodotti a base di maiale?',
      de: 'Enthält dieses Gericht Schweinefleisch oder Schweineprodukte?',
      pt: 'Este prato contém carne de porco ou derivados?',
      ja: 'この料理に豚肉や豚由来の食材は入っていますか？',
      zh: '这道菜含有猪肉或猪肉制品吗？',
      'zh-TW': '這道菜含有豬肉或豬肉製品嗎？',
      ko: '이 음식에 돼지고기나 돼지고기 제품이 들어있나요?',
      th: 'อาหารจานนี้มีหมูหรือผลิตภัณฑ์จากหมูไหมคะ/ครับ',
      vi: 'Món này có chứa thịt heo hay sản phẩm từ thịt heo không?',
    },
    strict: {
      en: 'I do not eat pork for religious reasons. Please confirm with the kitchen this dish has no pork — including bacon, lard, gelatin, or pork stock.',
      es: 'No como cerdo por motivos religiosos. Por favor confirme con la cocina que este plato no contiene cerdo, incluyendo bacon, manteca, gelatina o caldo de cerdo.',
      fr: 'Je ne mange pas de porc pour raisons religieuses. Pourriez-vous confirmer avec la cuisine que ce plat ne contient pas de porc — y compris bacon, saindoux, gélatine ou bouillon de porc ?',
      it: 'Non mangio maiale per motivi religiosi. Potrebbe confermare con la cucina che questo piatto non contiene maiale — incluso bacon, strutto, gelatina o brodo di maiale?',
      de: 'Ich esse aus religiösen Gründen kein Schweinefleisch. Bitte bestätigen Sie mit der Küche, dass dieses Gericht kein Schweinefleisch enthält — auch keinen Speck, Schmalz, keine Gelatine und keine Schweinebrühe.',
      pt: 'Não como porco por motivos religiosos. Por favor confirme com a cozinha que este prato não contém porco — incluindo bacon, banha, gelatina ou caldo de porco.',
      ja: '宗教上の理由で豚肉を食べません。ベーコン・ラード・ゼラチン・豚骨スープを含めて、この料理に豚由来の食材が含まれていないか、厨房に確認していただけますか？',
      zh: '我因宗教原因不吃猪肉。请向厨房确认这道菜不含任何猪肉制品，包括培根、猪油、明胶和猪骨汤。',
      'zh-TW': '我因宗教原因不吃豬肉。請向廚房確認這道菜不含任何豬肉製品，包括培根、豬油、明膠和豬骨湯。',
      ko: '저는 종교상 돼지고기를 먹지 않습니다. 베이컨, 라드, 젤라틴, 돼지뼈 육수를 포함해 이 음식에 돼지고기 제품이 없는지 주방에 확인해 주실 수 있나요?',
      th: 'ฉันไม่ทานหมูด้วยเหตุผลทางศาสนา กรุณาตรวจสอบกับห้องครัวว่าอาหารจานนี้ไม่มีหมู รวมถึงเบคอน น้ำมันหมู เจลาติน หรือซุปกระดูกหมู',
      vi: 'Tôi không ăn thịt heo vì lý do tôn giáo. Vui lòng xác nhận với bếp rằng món này không có thịt heo — kể cả thịt xông khói, mỡ heo, gelatin hay nước hầm xương heo.',
    },
    strictByReason: {
      halal: {
        en: 'I keep halal — pork is forbidden by my faith. Please confirm with the kitchen this dish has no pork — including bacon, lard, gelatin, or pork stock.',
      },
      kosher: {
        en: 'I keep kosher — pork is forbidden by my faith. Please confirm with the kitchen this dish has no pork — including bacon, lard, gelatin, or pork stock.',
      },
    },
  },

  no_shellfish: {
    preference: {
      en: 'Does this dish contain shellfish? (shrimp, crab, lobster, oyster, mussel)',
      es: '¿Este plato contiene mariscos? (camarón, cangrejo, langosta, ostra, mejillón)',
      fr: 'Ce plat contient-il des fruits de mer ? (crevettes, crabe, homard, huître, moule)',
      it: 'Questo piatto contiene crostacei o molluschi? (gamberi, granchio, aragosta, ostriche, cozze)',
      de: 'Enthält dieses Gericht Schalentiere? (Garnelen, Krabben, Hummer, Austern, Muscheln)',
      pt: 'Este prato contém marisco? (camarão, caranguejo, lagosta, ostra, mexilhão)',
      ja: 'この料理に甲殻類や貝類は入っていますか？（エビ・カニ・ロブスター・牡蠣・ムール貝）',
      zh: '这道菜含有贝类吗？（虾、蟹、龙虾、牡蛎、贻贝）',
      'zh-TW': '這道菜含有貝類嗎？（蝦、蟹、龍蝦、牡蠣、淡菜）',
      ko: '이 음식에 갑각류나 조개류가 들어있나요? (새우, 게, 랍스터, 굴, 홍합)',
      th: 'อาหารจานนี้มีอาหารทะเลประเภทกุ้ง ปู หอย ไหมคะ/ครับ',
      vi: 'Món này có chứa hải sản có vỏ không? (tôm, cua, tôm hùm, hàu, vẹm)',
    },
    strict: {
      en: 'I have a severe shellfish allergy. Please confirm with the kitchen this has no shrimp, crab, lobster, oyster, mussel, or shellfish stock — and is not cooked in the same oil or pan as those.',
      es: 'Tengo una alergia grave a los mariscos. Por favor confirme con la cocina que no contiene camarón, cangrejo, langosta, ostra, mejillón ni caldo de marisco — y que no se cocina en el mismo aceite o sartén que esos.',
      fr: 'J’ai une allergie grave aux fruits de mer. Pourriez-vous confirmer avec la cuisine que ce plat ne contient ni crevettes, ni crabe, ni homard, ni huîtres, ni moules, ni bouillon de fruits de mer — et n’est pas cuit dans la même huile ou poêle que ceux-ci ?',
      it: 'Ho una grave allergia ai crostacei e molluschi. Potrebbe confermare con la cucina che non contiene gamberi, granchio, aragosta, ostriche, cozze o brodo di crostacei — e non è cotto nello stesso olio o padella di quelli?',
      de: 'Ich habe eine schwere Schalentierallergie. Bitte bestätigen Sie mit der Küche, dass dieses Gericht keine Garnelen, Krabben, Hummer, Austern, Muscheln oder Schalentierbrühe enthält und nicht im selben Öl oder in der selben Pfanne wie diese zubereitet wird.',
      pt: 'Tenho alergia grave a marisco. Por favor confirme com a cozinha que não contém camarão, caranguejo, lagosta, ostra, mexilhão ou caldo de marisco — e não é preparado no mesmo óleo ou frigideira que esses.',
      ja: '甲殻類・貝類に重度のアレルギーがあります。エビ・カニ・ロブスター・牡蠣・ムール貝・甲殻類の出汁が含まれず、それらと同じ油や鍋で調理されていないか、厨房に確認していただけますか？',
      zh: '我对贝类有严重过敏。请向厨房确认不含虾、蟹、龙虾、牡蛎、贻贝或贝类高汤，且没有用与这些食材相同的油或锅烹调。',
      'zh-TW': '我對貝類有嚴重過敏。請向廚房確認不含蝦、蟹、龍蝦、牡蠣、淡菜或貝類高湯，且沒有用與這些食材相同的油或鍋烹調。',
      ko: '갑각류·조개류에 심한 알레르기가 있습니다. 새우, 게, 랍스터, 굴, 홍합, 해산물 육수가 없고, 이들과 같은 기름이나 팬에서 조리되지 않았는지 주방에 확인해 주실 수 있나요?',
      th: 'ฉันแพ้กุ้ง ปู หอยอย่างรุนแรง กรุณาตรวจสอบกับห้องครัวว่าไม่มีกุ้ง ปู ล็อบสเตอร์ หอยนางรม หอยแมลงภู่ หรือน้ำซุปอาหารทะเล และไม่ได้ทอดในน้ำมันหรือกระทะเดียวกับวัตถุดิบเหล่านี้',
      vi: 'Tôi bị dị ứng nặng với hải sản có vỏ. Vui lòng xác nhận với bếp rằng món này không có tôm, cua, tôm hùm, hàu, vẹm hay nước dùng từ hải sản — và không được nấu trong cùng chảo dầu với chúng.',
    },
    strictByReason: {
      // Rachel's complaint: kosher users don't have a "severe allergy" — they
      // have a religious rule. Honest framing.
      kosher: {
        en: 'I keep kosher — shellfish is not permitted in my diet. Please confirm with the kitchen this has no shrimp, crab, lobster, oyster, mussel, or shellfish stock.',
      },
    },
  },

  no_alcohol: {
    preference: {
      en: 'Is this dish cooked with alcohol? (wine, beer, sake, mirin)',
      es: '¿Este plato está cocinado con alcohol? (vino, cerveza, sake, mirin)',
      fr: 'Ce plat est-il cuisiné avec de l’alcool ? (vin, bière, saké, mirin)',
      it: 'Questo piatto è cucinato con alcol? (vino, birra, sake, mirin)',
      de: 'Wird dieses Gericht mit Alkohol gekocht? (Wein, Bier, Sake, Mirin)',
      pt: 'Este prato é cozinhado com álcool? (vinho, cerveja, sake, mirin)',
      ja: 'この料理にアルコールは使われていますか？（ワイン・ビール・酒・みりん）',
      zh: '这道菜烹调时使用酒类吗？（葡萄酒、啤酒、清酒、味醂）',
      'zh-TW': '這道菜烹調時使用酒類嗎？（葡萄酒、啤酒、清酒、味醂）',
      ko: '이 음식은 술로 요리하나요? (와인, 맥주, 사케, 미림)',
      th: 'อาหารจานนี้ปรุงด้วยแอลกอฮอล์ไหมคะ/ครับ (ไวน์ เบียร์ สาเก มิริน)',
      vi: 'Món này có nấu với rượu không? (vang, bia, rượu sake, mirin)',
    },
    strict: {
      en: 'I do not consume alcohol for religious or medical reasons. Please confirm with the kitchen this dish is prepared without any wine, beer, sake, mirin, or other alcohol — including in sauces or marinades.',
      es: 'No consumo alcohol por motivos religiosos o médicos. Por favor confirme con la cocina que este plato se prepara sin vino, cerveza, sake, mirin ni otro alcohol — incluyendo en salsas o adobos.',
      fr: 'Je ne consomme pas d’alcool pour raisons religieuses ou médicales. Pourriez-vous confirmer avec la cuisine que ce plat est préparé sans vin, bière, saké, mirin ou autre alcool — y compris dans les sauces ou marinades ?',
      it: 'Non consumo alcol per motivi religiosi o medici. Potrebbe confermare con la cucina che questo piatto è preparato senza vino, birra, sake, mirin o altro alcol — anche nelle salse o marinate?',
      de: 'Ich konsumiere aus religiösen oder medizinischen Gründen keinen Alkohol. Bitte bestätigen Sie mit der Küche, dass dieses Gericht ohne Wein, Bier, Sake, Mirin oder anderen Alkohol zubereitet wird — auch nicht in Soßen oder Marinaden.',
      pt: 'Não consumo álcool por motivos religiosos ou médicos. Por favor confirme com a cozinha que este prato é preparado sem vinho, cerveja, sake, mirin ou outro álcool — incluindo em molhos ou marinadas.',
      ja: '宗教または医療上の理由でアルコールを摂取しません。ソースやマリネ液を含めて、この料理がワイン・ビール・酒・みりん・その他のアルコールを使わずに調理されているか、厨房に確認していただけますか？',
      zh: '我因宗教或健康原因不饮酒。请向厨房确认这道菜的烹调中没有使用任何葡萄酒、啤酒、清酒、味醂或其他酒类——包括酱汁和腌料。',
      'zh-TW': '我因宗教或健康原因不飲酒。請向廚房確認這道菜的烹調中沒有使用任何葡萄酒、啤酒、清酒、味醂或其他酒類——包括醬汁和醃料。',
      ko: '저는 종교 또는 의료상의 이유로 술을 섭취하지 않습니다. 소스나 양념을 포함해 와인, 맥주, 사케, 미림 또는 기타 알코올이 사용되지 않았는지 주방에 확인해 주실 수 있나요?',
      th: 'ฉันไม่ดื่มแอลกอฮอล์ด้วยเหตุผลทางศาสนาหรือสุขภาพ กรุณาตรวจสอบกับห้องครัวว่าอาหารจานนี้ปรุงโดยไม่ใช้ไวน์ เบียร์ สาเก มิริน หรือแอลกอฮอล์อื่นๆ รวมถึงในซอสและน้ำหมัก',
      vi: 'Tôi không uống rượu vì lý do tôn giáo hoặc sức khỏe. Vui lòng xác nhận với bếp rằng món này được nấu không có rượu vang, bia, sake, mirin hay bất kỳ loại rượu nào — kể cả trong nước sốt hay nước ướp.',
    },
    strictByReason: {
      halal: {
        en: 'I keep halal — alcohol is forbidden by my faith. Please confirm with the kitchen this dish is prepared without any wine, beer, sake, mirin, or other alcohol — including in sauces, marinades, and vanilla extract.',
      },
    },
  },

  no_fish_sauce: {
    preference: {
      en: 'Does this dish contain fish sauce, shrimp paste, or oyster sauce?',
      es: '¿Este plato contiene salsa de pescado, pasta de camarón o salsa de ostras?',
      fr: 'Ce plat contient-il de la sauce poisson, de la pâte de crevettes ou de la sauce huître ?',
      it: 'Questo piatto contiene salsa di pesce, pasta di gamberi o salsa di ostriche?',
      de: 'Enthält dieses Gericht Fischsauce, Garnelenpaste oder Austernsauce?',
      pt: 'Este prato contém molho de peixe, pasta de camarão ou molho de ostra?',
      ja: 'この料理にナンプラー（魚醤）・シュリンプペースト・オイスターソースは入っていますか？',
      zh: '这道菜含有鱼露、虾酱或蚝油吗？',
      'zh-TW': '這道菜含有魚露、蝦醬或蠔油嗎？',
      ko: '이 음식에 피쉬소스, 새우장, 굴소스가 들어있나요?',
      th: 'อาหารจานนี้มีน้ำปลา กะปิ หรือซอสหอยนางรมไหมคะ/ครับ',
      vi: 'Món này có chứa nước mắm, mắm tôm hay dầu hào không?',
    },
    strict: {
      en: 'I am vegetarian — please confirm with the kitchen this contains no fish sauce, shrimp paste, oyster sauce, or any seafood-based seasoning.',
      es: 'Soy vegetariano — por favor confirme con la cocina que no contiene salsa de pescado, pasta de camarón, salsa de ostras ni ningún condimento a base de marisco.',
      fr: 'Je suis végétarien — pourriez-vous confirmer avec la cuisine que ce plat ne contient ni sauce poisson, ni pâte de crevettes, ni sauce huître, ni aucun assaisonnement à base de fruits de mer ?',
      it: 'Sono vegetariano — potrebbe confermare con la cucina che non contiene salsa di pesce, pasta di gamberi, salsa di ostriche o alcun condimento a base di pesce o crostacei?',
      de: 'Ich bin Vegetarier — bitte bestätigen Sie mit der Küche, dass dieses Gericht keine Fischsauce, Garnelenpaste, Austernsauce oder andere Gewürze auf Meeresfrüchtebasis enthält.',
      pt: 'Sou vegetariano — por favor confirme com a cozinha que não contém molho de peixe, pasta de camarão, molho de ostra ou qualquer tempero à base de marisco.',
      ja: 'ベジタリアンです — ナンプラー（魚醤）・シュリンプペースト・オイスターソース・その他の魚介ベースの調味料が含まれていないか、厨房に確認していただけますか？',
      zh: '我是素食者——请向厨房确认这道菜不含鱼露、虾酱、蚝油或任何海鲜类调味品。',
      'zh-TW': '我是素食者——請向廚房確認這道菜不含魚露、蝦醬、蠔油或任何海鮮類調味品。',
      ko: '저는 채식주의자입니다 — 피쉬소스, 새우장, 굴소스 또는 해산물 기반 양념이 없는지 주방에 확인해 주실 수 있나요?',
      th: 'ฉันทานมังสวิรัติ — กรุณาตรวจสอบกับห้องครัวว่าไม่มีน้ำปลา กะปิ ซอสหอยนางรม หรือเครื่องปรุงที่ทำจากอาหารทะเลใดๆ',
      vi: 'Tôi ăn chay — vui lòng xác nhận với bếp rằng món này không có nước mắm, mắm tôm, dầu hào hay bất kỳ gia vị nào từ hải sản.',
    },
  },

  no_pork_lard: {
    strict: {
      en: 'Please confirm with the kitchen this dish is not cooked with pork lard or pork fat. (Often used invisibly in beans, tortillas, refried dishes, and stews.)',
      es: 'Por favor confirme con la cocina que este plato no se cocina con manteca de cerdo. (A menudo usada de forma invisible en frijoles, tortillas, refritos y guisos.)',
      fr: 'Pourriez-vous confirmer avec la cuisine que ce plat n’est pas cuisiné avec du saindoux ou de la graisse de porc ? (Souvent utilisé invisiblement dans les haricots, tortillas, plats sautés et ragoûts.)',
      it: 'Potrebbe confermare con la cucina che questo piatto non è cucinato con strutto o grasso di maiale? (Spesso usato in modo invisibile in fagioli, tortillas, piatti fritti e stufati.)',
      de: 'Bitte bestätigen Sie mit der Küche, dass dieses Gericht nicht mit Schweineschmalz oder Schweinefett zubereitet wird. (Oft unsichtbar in Bohnen, Tortillas, frittierten Gerichten und Eintöpfen verwendet.)',
      pt: 'Por favor confirme com a cozinha que este prato não é cozinhado com banha ou gordura de porco. (Frequentemente usada de forma invisível em feijões, tortillas, fritos e ensopados.)',
      ja: 'この料理が豚のラード（豚脂）で調理されていないか、厨房に確認していただけますか？（豆料理・トルティーヤ・揚げ物・煮込みに知らずに使われていることがあります。）',
      zh: '请向厨房确认这道菜没有用猪油烹调。（在豆类、玉米饼、回锅菜和炖菜中经常被悄无声息地使用。）',
      'zh-TW': '請向廚房確認這道菜沒有用豬油烹調。（在豆類、玉米餅、回鍋菜和燉菜中經常被悄無聲息地使用。）',
      ko: '이 음식이 돼지 라드나 돼지 지방으로 조리되지 않았는지 주방에 확인해 주실 수 있나요? (콩 요리, 토르티야, 볶음, 스튜에 모르게 들어가는 경우가 많습니다.)',
      th: 'กรุณาตรวจสอบกับห้องครัวว่าอาหารจานนี้ไม่ได้ปรุงด้วยน้ำมันหมูหรือมันหมู (มักใส่โดยไม่บอกในอาหารถั่ว แป้งตอร์ตียา อาหารผัด และสตูว์)',
      vi: 'Vui lòng xác nhận với bếp rằng món này không nấu với mỡ heo. (Thường được dùng âm thầm trong các món đậu, bánh tortilla, món chiên xào và món hầm.)',
    },
  },

  no_cross_contamination: {
    strict: {
      en: 'Please confirm this dish is not cooked in the same oil, pan, or surface as meat, fish, or shellfish. Cross-contamination is a serious problem for me.',
      es: 'Por favor confirme que este plato no se cocina en el mismo aceite, sartén o superficie que carne, pescado o marisco. La contaminación cruzada es un problema serio para mí.',
      fr: 'Pourriez-vous confirmer que ce plat n’est pas cuit dans la même huile, poêle ou surface que de la viande, du poisson ou des fruits de mer ? La contamination croisée est un problème sérieux pour moi.',
      it: 'Potrebbe confermare che questo piatto non è cotto nello stesso olio, padella o superficie di carne, pesce o crostacei? La contaminazione crociata è un problema serio per me.',
      de: 'Bitte bestätigen Sie, dass dieses Gericht nicht im selben Öl, in der selben Pfanne oder auf der selben Oberfläche wie Fleisch, Fisch oder Schalentiere zubereitet wird. Kreuzkontamination ist für mich ein ernstes Problem.',
      pt: 'Por favor confirme que este prato não é cozinhado no mesmo óleo, frigideira ou superfície que carne, peixe ou marisco. A contaminação cruzada é um problema sério para mim.',
      ja: 'この料理が肉・魚・甲殻類と同じ油・鍋・調理面で作られていないか、ご確認いただけますか？交差汚染は私にとって深刻な問題です。',
      zh: '请确认这道菜没有在与肉、鱼或贝类相同的油、锅或台面上烹调。对我来说，交叉污染是个严重问题。',
      'zh-TW': '請確認這道菜沒有在與肉、魚或貝類相同的油、鍋或檯面上烹調。對我來說，交叉污染是個嚴重問題。',
      ko: '이 음식이 고기, 생선, 갑각류와 같은 기름, 팬, 조리면에서 만들어지지 않았는지 확인해 주실 수 있나요? 교차오염은 저에게 심각한 문제입니다.',
      th: 'กรุณายืนยันว่าอาหารจานนี้ไม่ได้ปรุงในน้ำมัน กระทะ หรือพื้นผิวเดียวกับเนื้อ ปลา หรือกุ้ง/ปู/หอย การปนเปื้อนข้ามเป็นปัญหาร้ายแรงสำหรับฉัน',
      vi: 'Vui lòng xác nhận rằng món này không được nấu trong cùng chảo dầu, chảo hoặc bề mặt đã dùng cho thịt, cá hay hải sản có vỏ. Việc nhiễm chéo là vấn đề nghiêm trọng đối với tôi.',
    },
  },
};

export interface LocalizedPhrase {
  text: string;
  lang: string;
  translated: boolean;
}

function resolveLangKey(sourceLanguage: string): string {
  return findLanguage(sourceLanguage)?.code ?? sourceLanguage;
}

function pickFromTable(table: LangMap, sourceLanguage: string): LocalizedPhrase | null {
  const key = resolveLangKey(sourceLanguage);
  if (table[key]) return { text: table[key]!, lang: key, translated: true };
  if (table.en) return { text: table.en, lang: 'en', translated: false };
  return null;
}

export function dietaryQuestionFor(
  tag: DietaryConcernTag,
  severity: DietaryConcernSeverity,
  sourceLanguage: string,
  reason?: DietaryConcernReason | null,
): LocalizedPhrase | null {
  const phrasesBySeverity = PHRASES[tag];
  if (!phrasesBySeverity) return null;

  // For strict severity with a captured reason, try the reason variant first
  // — but ONLY if it has native coverage of the source language. Otherwise we
  // fall through to the regular strict table, which has 12 QA'd languages.
  // Showing English-with-religious-framing instead of a native-language
  // strict copy would be a regression: native > reason-specific.
  if (severity === 'strict' && reason) {
    const reasonMap = phrasesBySeverity.strictByReason?.[reason];
    if (reasonMap) {
      const key = resolveLangKey(sourceLanguage);
      if (reasonMap[key]) {
        return { text: reasonMap[key]!, lang: key, translated: true };
      }
    }
  }

  const primary = phrasesBySeverity[severity];
  if (primary) {
    const picked = pickFromTable(primary, sourceLanguage);
    if (picked) return picked;
  }
  // No entry for this severity (or no coverage at all) — fall back to the
  // other severity table (e.g. cross-contamination and lard are strict-only).
  const alt = severity === 'strict' ? phrasesBySeverity.preference : phrasesBySeverity.strict;
  if (!alt) return null;
  return pickFromTable(alt, sourceLanguage);
}

// Consolidated religious-dietary phrase shown on the hand-off screen when a
// halal or kosher user has multiple strict concerns on a single dish — one
// coherent ask the waiter reads once, instead of three red cards. Each
// concern is implicitly covered by the consolidated phrase. ENGLISH-only
// today; per-language QA will land variants later.
const RELIGIOUS_COMBINED: Record<'halal' | 'kosher', LangMap> = {
  halal: {
    en: 'I keep halal. Pork, alcohol, and pork-derived ingredients (gelatin, lard, broth) are forbidden by my faith. If this dish contains meat, it must come from a halal-slaughtered animal (zabihah). Please confirm with the kitchen.',
  },
  kosher: {
    en: 'I keep kosher. Pork, shellfish, and non-kosher ingredients are forbidden by my faith. If this dish contains meat, it must come from a kosher-certified source, and meat must not be mixed with dairy. Please confirm with the kitchen, including separate prep surfaces if needed.',
  },
};

export function religiousCombinedFor(
  reason: 'halal' | 'kosher',
  sourceLanguage: string,
): LocalizedPhrase {
  const map = RELIGIOUS_COMBINED[reason];
  const key = resolveLangKey(sourceLanguage);
  if (map[key]) return { text: map[key]!, lang: key, translated: true };
  return { text: map.en!, lang: 'en', translated: false };
}

// Concern tag → user-language UI label (English seed; full i18n in i18n.ts via t()).
export const DIETARY_TAG_LABELS: Record<DietaryConcernTag, string> = {
  vegan: 'Vegan',
  vegetarian: 'Vegetarian',
  gluten_free: 'Gluten-free',
  no_nuts: 'No nuts',
  no_dairy: 'No dairy',
  no_pork: 'No pork',
  no_shellfish: 'No shellfish',
  no_alcohol: 'No alcohol',
  no_fish_sauce: 'No fish sauce',
  no_pork_lard: 'No pork lard',
  no_cross_contamination: 'No cross-contamination',
};

// Maps a strict dietary tag to the dish-side allergens it conflicts with.
// Used by the contradiction guard in DishSheet — if the dish has any of these
// in confirmed `dish.allergens` and the user has the strict tag, block the add.
import type { Allergen } from '../../shared/menu';

export const TAG_TO_CONFLICTING_ALLERGENS: Partial<Record<DietaryConcernTag, Allergen[]>> = {
  no_nuts: ['nuts', 'peanut'],
  no_dairy: ['dairy'],
  no_pork: ['pork'],
  no_shellfish: ['shellfish'],
  no_alcohol: ['alcohol'],
  // celiac users tagging gluten_free strict on a wheat-flagged dish should
  // hit the contradiction guard. Hidden-gluten sources (barley, rye, soy
  // sauce) are not enumerated as Allergen values today and remain a
  // confirm-with-kitchen concern surfaced via the strict phrasebook copy.
  gluten_free: ['wheat'],
  // Strict vegan/vegetarian conflict with confirmed animal allergens. Hidden
  // ingredients (fish sauce, stock cubes, lard) remain a confirm-with-kitchen
  // concern surfaced via the strict phrasebook copy and the no_fish_sauce /
  // no_pork_lard tags.
  vegan: ['fish', 'shellfish', 'dairy', 'egg', 'pork'],
  vegetarian: ['fish', 'shellfish', 'pork'],
};

// Given the strict dietary tags on a cart line and the dish's confirmed
// allergens, returns the allergens that the user explicitly overrode. Used
// by the hand-off screen to render the kitchen-confirm warning so the
// waiter knows which specific allergens were flagged in the menu but
// allowed by the user.
export function computeOverrideAllergens(
  dietaryTags: DietaryConcern[],
  dishAllergens: Allergen[],
): Allergen[] {
  const conflicts = new Set<Allergen>();
  for (const concern of dietaryTags) {
    if (concern.severity !== 'strict') continue;
    const banned = TAG_TO_CONFLICTING_ALLERGENS[concern.tag];
    if (!banned) continue;
    for (const a of banned) {
      if (dishAllergens.includes(a)) conflicts.add(a);
    }
  }
  return Array.from(conflicts);
}
